/**
 * What a book looks like in the library before anybody has drawn it a cover.
 *
 * Every book gets a picture, because a grid with holes in it is worse than a
 * list: a missing tile reads as a loading failure, and a row of identical grey
 * boxes reads as one book repeated. So a book with no cover gets a generated
 * one — a gradient and its own initial — that is *stable*, because a tile that
 * changed colour between two visits would be a different book to the eye.
 *
 * Stable means derived from the id, not from the index in the list and not
 * from a random seed. The id is the only thing about a book that never
 * changes; a title-derived colour would shift the moment the title was edited.
 */

/**
 * PLACEHOLDER_RAMPS are the gradients a coverless book can be given.
 *
 * All within the mark's own range — sage through sky, with the deeper greens
 * and one clay for warmth — so a shelf of them reads as one set rather than as
 * a colour picker. Each is dark enough for white type at the sizes the grid
 * uses, which is the constraint that decides the list: a pale ramp needs dark
 * type, and two kinds of tile side by side look like a mistake.
 */
export const PLACEHOLDER_RAMPS: [string, string][] = [
  ["#4a7a2a", "#3f93b8"], // sage-deep → sky, the mark's own ramp
  ["#2f5d3a", "#6b9d3e"], // deep green
  ["#1f4a5c", "#3f93b8"], // deep sky
  ["#5c4a2a", "#9d7a3e"], // clay
  ["#3a3f6b", "#5f7fb8"], // indigo
  ["#5c2f3a", "#9d5060"], // wine
];

/**
 * rampFor picks a book's gradient from its id.
 *
 * A sum of the id's code units rather than a real hash: the id is a UUID, the
 * set is six wide, and the only property needed is that the same book is
 * always the same colour. A cryptographic hash here would be a dependency
 * bought to choose a green.
 */
export function rampFor(id: string): [string, string] {
  let total = 0;
  for (let i = 0; i < id.length; i++) total += id.charCodeAt(i);
  return PLACEHOLDER_RAMPS[total % PLACEHOLDER_RAMPS.length]!;
}

/**
 * initialOf is the letter drawn on a coverless tile.
 *
 * The first *letter*, not the first character, and it may be some way in.
 * Titles start with quotation marks and dashes — «بوف کور» — and a tile
 * showing a guillemet says nothing about the book. Emoji and other symbols are
 * skipped past for the same reason; a title that is nothing but an emoji keeps
 * it, because something is better than a blank square.
 *
 * Walked as graphemes through Intl.Segmenter where there is one, because a
 * "character" is not a code unit and often not a code point either: a letter
 * with a combining mark above it, or an emoji, is several code units, and
 * taking one of them draws a replacement box.
 */
export function initialOf(title: string): string {
  const trimmed = title.trim();
  let first = "";
  for (const grapheme of graphemes(trimmed)) {
    if (!first) first = grapheme;
    if (/\p{Letter}|\p{Number}/u.test(grapheme)) return grapheme;
  }
  return first || "?";
}

function graphemes(text: string): Iterable<string> {
  if (!text) return [];
  if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    return Array.from(segmenter.segment(text), (s) => s.segment);
  }
  return Array.from(text);
}
